'use client';

import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { ThemeProvider } from 'next-themes';
import { useEffect, useState } from 'react';

const QueryProvider = dynamic(() => import('@/lib/query-provider'));

/**
 * Default QueryClient for the app.
 *
 * - `staleTime: 60s` keeps cache warm but not stale for long-lived dashboards.
 * - `retry: 1` avoids hammering failing endpoints.
 * - A global `MutationCache` surfaces unhandled mutation errors via
 *   `toastError`, so hooks that forget their own `onError` still get a
 *   sanitized user-facing toast. Mutations that want to suppress the default
 *   toast can pass `meta: { silent: true }` to their `useMutation` call.
 *
 * Org-switch cache reset is handled in `components/layout/AppLayout.tsx`
 * (cancelQueries + removeQueries when `organization?.id` changes).
 */
export function Providers({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [needsQueries, setNeedsQueries] = useState(pathname !== '/');
  // The landing page has no queries. Once the app is entered, retain its cache
  // even if the user returns home, so back navigation never loses warm data.
  if (!needsQueries && pathname !== '/') setNeedsQueries(true);

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    // Radix owns the body lock counter (including nested dialogs and menus).
    // Mirror it onto the root: body overflow must stay clipped for sticky chrome.
    // Use a class because Turbopack drops attribute selectors inside :has().
    const syncScrollLock = () => {
      root.classList.toggle(
        'document-scroll-locked',
        body.hasAttribute('data-scroll-locked')
      );
    };
    const observer = new MutationObserver(syncScrollLock);
    observer.observe(body, {
      attributes: true,
      attributeFilter: ['data-scroll-locked'],
    });
    syncScrollLock();
    return () => {
      observer.disconnect();
      root.classList.remove('document-scroll-locked');
    };
  }, []);

  // Eclipse is a light-mode-only product. `forcedTheme="light"` makes
  // next-themes ignore any stored preference or OS-level dark mode and
  // never apply the `.dark` class to <html>.
  return (
    <ThemeProvider
      attribute="class"
      forcedTheme="light"
      enableSystem={false}
      disableTransitionOnChange
    >
      {needsQueries ? <QueryProvider>{children}</QueryProvider> : children}
    </ThemeProvider>
  );
}
